import { cn } from '@mdxui/primitives/lib/utils'
import { ArrowRight } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'

export interface DisplayAdLinkProps {
  href: string
  children: ReactNode
  className?: string
}

export interface DisplayAdProps {
  /** Ad title (default: 'Upgrade Your Experience') */
  title?: string
  /** Ad description (default: 'Discover premium features...') */
  description?: string
  /** CTA button text (default: 'Learn More') */
  ctaText?: string
  /** CTA button href (default: '#') */
  ctaHref?: string
  /** Optional highlight/badge text */
  highlight?: string
  /** Custom link component */
  LinkComponent?: ComponentType<DisplayAdLinkProps>
  /** Additional className */
  className?: string
}

function DefaultLink({ href, children, className }: DisplayAdLinkProps) {
  return (
    <a href={href} className={className}>
      {children}
    </a>
  )
}

export function DisplayAd({
  title = 'Upgrade Your Experience',
  description = 'Discover premium features and take your project to the next level.',
  ctaText = 'Learn More',
  ctaHref = '#',
  highlight,
  LinkComponent = DefaultLink,
  className,
}: DisplayAdProps) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-[var(--radius)] border border-border bg-card p-6 shadow-sm',
        className
      )}
    >
      {/* Accent line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />

      <div className="flex flex-col gap-4">
        {highlight && (
          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
            {highlight}
          </span>
        )}

        <div className="space-y-3">
          <h3 className="text-xl font-bold text-card-foreground leading-tight">{title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
        </div>

        <LinkComponent
          href={ctaHref}
          className="group inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-primary-foreground bg-primary hover:bg-primary/90 rounded-[var(--radius)] transition-all duration-200 hover:shadow-md w-full mt-2"
        >
          <span>{ctaText}</span>
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </LinkComponent>
      </div>
    </div>
  )
}
